import PropTypes from 'prop-types';
import RevealConfirmCard from './RevealConfirmCard';
import RevealProgressCard from './RevealProgressCard';
import { AGENT_ENRICHMENT_ENABLED } from '../../../utils/constants/featureFlags';

/**
 * Inline dispatcher for the agent enrichment cards (TD-2293).
 *
 * Reads the parsed `message.enrichment` payload (set by the adapter from the
 * agent's tags) and renders the matching card under the message's markdown.
 * Gated centrally on AGENT_ENRICHMENT_ENABLED — while the flag is off, tags
 * are still stripped from chat text upstream but no card renders.
 *
 * Card types: revealPending → confirm-before-spend card (shipped TD-2281).
 * revealJob (bulk progress), discovery and options render in later phases.
 */
const AgentEnrichmentCards = ({
  message,
  onControlReply = null,
  onRevealComplete = null,
  creditLimit = null
}) => {
  if (!AGENT_ENRICHMENT_ENABLED) return null;
  const enrichment = message?.enrichment;
  if (!enrichment) return null;

  return (
    <div className="agent-enrichment-cards">
      {enrichment.revealPending && (
        <RevealConfirmCard
          revealPending={enrichment.revealPending}
          messageTimestamp={message.timestamp}
          onControlReply={onControlReply}
          creditLimit={creditLimit}
        />
      )}
      {enrichment.revealJob && (
        <RevealProgressCard
          jobId={enrichment.revealJob}
          onControlReply={onControlReply}
          onRevealComplete={onRevealComplete}
        />
      )}
    </div>
  );
};

AgentEnrichmentCards.propTypes = {
  /** Chat message carrying a parsed `enrichment` payload. */
  message: PropTypes.shape({
    enrichment: PropTypes.object,
    timestamp: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date)
    ])
  }),
  /** Sends a hidden control reply into this message's session. */
  onControlReply: PropTypes.func,
  /** Called when a reveal job settles live, to reconcile the credit chip. */
  onRevealComplete: PropTypes.func,
  /** Total contact-credit allowance, shown as the balance-after total (TD-2884). */
  creditLimit: PropTypes.number
};

export default AgentEnrichmentCards;
